import { Component, OnInit, OnDestroy, Output, EventEmitter } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Subscription } from 'rxjs';
import { Router } from '@angular/router';
import { SignupStateService } from '../../services/signup-state.service';
import { ExpuestosPoliticamenteService } from 'src/app/auth/services/expuestos-politicamente.service';
import { UserService } from 'src/app/dashboard/services/user.service';
import { buildUserPayload } from '../../utils/build-user-payload';
import { mensajeDeErrorDeRegistro } from '../../utils/registration-error';
import {
  COUNTRY_PHONE_OPTIONS,
  PARENTESCO_OPTIONS,
  PEP_CARGOS,
  PEP_DECLARATION_LIST,
} from '../../constants/signup.constants';

@Component({
  selector: 'app-pep-declaration-step',
  templateUrl: './pep-declaration-step.component.html',
  styleUrls: ['./pep-declaration-step.component.sass'],
})
export class PepDeclarationStepComponent implements OnInit, OnDestroy {
  @Output() onError = new EventEmitter<string>();
  @Output() onSuccess = new EventEmitter<string>();

  pepForm!: FormGroup;
  loading = false;

  // Datos para selectores
  countries = COUNTRY_PHONE_OPTIONS;
  parentescoOptions = PARENTESCO_OPTIONS;
  cargosOptions = PEP_CARGOS;
  declarationList = PEP_DECLARATION_LIST;

  // Control para mostrar campo "otro parentesco"
  showOtherParentesco = false;

  private subscriptions: Subscription[] = [];

  constructor(
    private fb: FormBuilder,
    public signupState: SignupStateService,
    private pepService: ExpuestosPoliticamenteService,
    private userService: UserService,
    private router: Router
  ) {}

  ngOnInit(): void {
    this.initForm();
    this.restoreFormData();
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach(s => s.unsubscribe());
  }

  private initForm(): void {
    this.pepForm = this.fb.group({
      apellidoPaterno: ['', Validators.required],
      apellidoMaterno: ['', Validators.required],
      nombre: ['', Validators.required],
      nacionalidad: ['', Validators.required],
      documentNumber: ['', Validators.required],
      parentesco: ['', Validators.required],
      otroParentesco: [''],
      cargoOcupado: ['', Validators.required],
      confirmDeclaration: [false, Validators.requiredTrue],
    });

    // Escuchar cambio de parentesco
    const parentSub = this.pepForm.get('parentesco')?.valueChanges.subscribe((value) => {
      this.showOtherParentesco = value === 'otro';
      if (this.showOtherParentesco) {
        this.pepForm.get('otroParentesco')?.setValidators([Validators.required]);
      } else {
        this.pepForm.get('otroParentesco')?.clearValidators();
        this.pepForm.get('otroParentesco')?.setValue('');
      }
      this.pepForm.get('otroParentesco')?.updateValueAndValidity();

      // Auto-completar con datos OCR si es "yo_mismo"
      if (value === 'yo_mismo') {
        this.autoFillFromOcrData();
      }
    });
    if (parentSub) this.subscriptions.push(parentSub);
  }

  /**
   * Auto-completa el formulario con datos OCR cuando el usuario es la persona expuesta
   */
  private autoFillFromOcrData(): void {
    const ocrData = this.signupState.currentState.ocrData;
    if (!ocrData) return;

    // Separar apellidos si están juntos
    const apellidos = this.splitApellidos(ocrData.lastName || '');

    this.pepForm.patchValue({
      nombre: ocrData.firstName || '',
      apellidoPaterno: apellidos.paterno,
      apellidoMaterno: apellidos.materno,
      documentNumber: ocrData.documentNumberClean || ocrData.documentNumber || '',
      // Nacionalidad ya confirmada por el usuario en el paso anterior.
      // No caer a countryName (pais del documento): son cosas distintas.
      nacionalidad: ocrData.nationality || '',
    });
  }

  /**
   * Separa el string de apellidos en paterno y materno
   */
  private splitApellidos(apellidos: string): { paterno: string; materno: string } {
    const parts = apellidos.trim().split(/\s+/);
    if (parts.length >= 2) {
      return {
        paterno: parts[0],
        materno: parts.slice(1).join(' '),
      };
    }
    return {
      paterno: apellidos,
      materno: '',
    };
  }

  /**
   * Restaura datos del formulario si existen en el estado
   */
  private restoreFormData(): void {
    const savedData = this.signupState.pepDeclarationData;
    if (savedData) {
      this.pepForm.patchValue({
        apellidoPaterno: savedData.apellidoPaterno,
        apellidoMaterno: savedData.apellidoMaterno,
        nombre: savedData.nombre,
        nacionalidad: savedData.nacionalidad,
        documentNumber: savedData.documentNumber,
        parentesco: savedData.parentesco,
        cargoOcupado: savedData.cargoOcupado,
      });
    }
  }

  async onSubmit(): Promise<void> {
    if (this.pepForm.invalid) {
      this.markFormAsTouched();
      this.onError.emit('Por favor completa todos los campos requeridos.');
      return;
    }

    this.loading = true;

    try {
      const formData = this.pepForm.getRawValue();
      const ocrData = this.signupState.currentState.ocrData;

      // Determinar parentesco final
      const parentescoFinal = formData.parentesco === 'otro'
        ? formData.otroParentesco
        : formData.parentesco;

      // Guardar datos en estado
      const pepData = {
        apellidoPaterno: formData.apellidoPaterno,
        apellidoMaterno: formData.apellidoMaterno,
        nombre: formData.nombre,
        nacionalidad: formData.nacionalidad,
        documentNumber: formData.documentNumber,
        parentesco: parentescoFinal,
        cargoOcupado: formData.cargoOcupado,
      };

      this.signupState.setPepDeclarationData(pepData);

      // Guardar declaración PEP en backend
      await this.pepService.create({
        tipoDoc: ocrData?.documentType || '',
        document: ocrData?.documentNumberClean || ocrData?.documentNumber || '',
        dataInfo: {
          apellidoPaterno: pepData.apellidoPaterno,
          apellidoMaterno: pepData.apellidoMaterno,
          nombre: pepData.nombre,
          nacionalidad: pepData.nacionalidad,
          run: pepData.documentNumber,
          parentesco: pepData.parentesco,
          cargoOcupado: pepData.cargoOcupado,
        },
      });

      // Registrar usuario en backend
      await this.registerUser();

    } catch (err: any) {
      this.onError.emit(err?.message || 'Error al guardar. Intenta nuevamente.');
    } finally {
      this.loading = false;
    }
  }

  /**
   * Registra el usuario en el backend
   */
  private async registerUser(): Promise<void> {
    const user = buildUserPayload(
      this.signupState.currentState,
      this.signupState.documentType,
      true
    );

    try {
      await this.userService.sendUserVerification({ user });
      this.onSuccess.emit('Registro completado exitosamente');
      this.signupState.completeRegistration();
      this.router.navigate(['/auth/sign-in']);
    } catch (err: any) {
      throw new Error(mensajeDeErrorDeRegistro(err));
    }
  }

  private markFormAsTouched(): void {
    Object.keys(this.pepForm.controls).forEach((key) => {
      this.pepForm.get(key)?.markAsTouched();
    });
  }

  async goBack(): Promise<void> {
    // Guardar datos parciales antes de volver
    if (this.pepForm.dirty) {
      const formData = this.pepForm.getRawValue();
      const parentescoFinal = formData.parentesco === 'otro'
        ? formData.otroParentesco
        : formData.parentesco;

      this.signupState.setPepDeclarationData({
        apellidoPaterno: formData.apellidoPaterno,
        apellidoMaterno: formData.apellidoMaterno,
        nombre: formData.nombre,
        nacionalidad: formData.nacionalidad,
        documentNumber: formData.documentNumber,
        parentesco: parentescoFinal,
        cargoOcupado: formData.cargoOcupado,
      });
    }

    await this.signupState.prevStep();
  }

  // ==================== GETTERS PARA VALIDACION ====================

  isFieldInvalid(fieldName: string): boolean {
    const field = this.pepForm.get(fieldName);
    return field ? field.invalid && field.touched : false;
  }

  get isFormValid(): boolean {
    return this.pepForm.valid;
  }

  /**
   * Obtiene la bandera para un pais
   */
  getFlagForCountry(countryName: string): string {
    const country = this.countries.find(
      (c) => c.name.toLowerCase() === countryName?.toLowerCase()
    );
    return country ? country.flag : 'chile.png';
  }

  /**
   * Obtiene el label del parentesco seleccionado
   */
  getParentescoLabel(value: string): string {
    const option = this.parentescoOptions.find((p) => p.value === value);
    return option ? option.label : value;
  }
}
